import type { AnchorHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";

import { useLocale } from "@/i18n/locale-context";
import { cn } from "@/lib/utils";

interface ContentLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  children: ReactNode;
  download?: boolean | string;
  href: string;
}

function isExternalHref(href: string): boolean {
  return /^(https?:|mailto:|tel:)/.test(href);
}

/**
 * Locale-aware link wrapper that supports internal routes, external URLs, mailto,
 * tel links, and download targets through one content-driven interface.
 */
export function ContentLink({
  children,
  className,
  download,
  href,
  rel,
  target,
  ...props
}: ContentLinkProps): JSX.Element {
  const { localePath } = useLocale();
  const external = isExternalHref(href);

  if (external || download) {
    return (
      <a
        className={className}
        download={download}
        href={href}
        rel={rel ?? (external ? "noreferrer" : undefined)}
        target={target ?? (external ? "_blank" : undefined)}
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      className={cn(className)}
      to={localePath(href)}
      {...props}
    >
      {children}
    </Link>
  );
}
